import { Component, Input, Output, EventEmitter, forwardRef, OnInit } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, FormControl } from '@angular/forms';

@Component({
  selector: 'app-custom-multiselect',
  templateUrl: './custom-multiselect.component.html',
  styleUrls: ['./custom-multiselect.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomMultiselectComponent),
      multi: true
    }
  ]
})
export class CustomMultiselectComponent implements ControlValueAccessor, OnInit {
  @Input() options: any[] = [];
  @Input() optionLabel: string = 'label';
  @Input() optionValue: string = 'value';
  @Input() defaultLabel: string = 'choose';
  @Input() formControl: FormControl;
  @Input() formControlName: string;
  @Output() onChange = new EventEmitter<any>();

  selectedItems: any[] = [];
  selectedValues: any[] = [];
  filteredOptions: any[] = [];
  filterText: string = '';
  isOpen: boolean = false;
  disabled: boolean = false;

  // Para implementar ControlValueAccessor
  private onChangeCallback: (_: any) => void = () => { };
  private onTouchedCallback: () => void = () => { };

  ngOnInit() {
    this.filteredOptions = [...this.options];

    // Si hay un formControl asignado, sincronizar con él
    if (this.formControl) {
      this.formControl.valueChanges.subscribe(values => {
        if (values !== this.selectedValues) {
          this.updateSelectedFromValues(values);
        }
      });
    }
  }

  ngOnChanges(changes) {
    // Si las opciones cambiaron y ya tenemos valores seleccionados
    if (changes.options && !changes.options.firstChange &&
      this.selectedValues && this.selectedValues.length > 0) {
      this.updateSelectedFromValues(this.selectedValues);
    }
  }

  toggleDropdown() {
    if (this.disabled) return;

    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      // Resetear el filtro al abrir
      this.filterText = '';
      this.filteredOptions = [...this.options];
      // Esperar a que el DOM se actualice y enfocar el input
      setTimeout(() => {
        const filterInput = document.querySelector('.custom-multiselect-filter input');
        if (filterInput) {
          (filterInput as HTMLInputElement).focus();
        }
      }, 0);
    } else {
      this.onTouchedCallback();
    }
  }

  isSelected(option: any): boolean {
    return this.selectedItems.findIndex(item =>
      item[this.optionValue] === option[this.optionValue]) > -1;
  }

  toggleSelection(option: any, event: Event) {
    if (this.disabled) return;

    event.stopPropagation();

    const index = this.selectedItems.findIndex(item =>
      item[this.optionValue] === option[this.optionValue]);

    // Crear nuevas copias de los arreglos para asegurar detección de cambios
    let updatedItems = [...this.selectedItems];
    let updatedValues = [...this.selectedValues];

    if (index > -1) {
      updatedItems.splice(index, 1);
      const valueIndex = updatedValues.indexOf(option[this.optionValue]);
      if (valueIndex > -1) {
        updatedValues.splice(valueIndex, 1);
      }
    } else {
      updatedItems.push(option);
      updatedValues.push(option[this.optionValue]);
    }

    this.selectedItems = updatedItems;
    this.selectedValues = updatedValues;

    this.onChangeCallback(this.selectedValues);
    this.onChange.emit({
      originalEvent: event,
      value: this.selectedValues
    });
  }

  filterOptions() {
    if (!this.filterText.trim()) {
      this.filteredOptions = [...this.options];
      return;
    }

    const searchText = this.filterText.toLowerCase();
    this.filteredOptions = this.options.filter(option =>
      option[this.optionLabel].toLowerCase().includes(searchText)
    );
  }

  updateSelectedFromValues(values: any[]) {
    if (!values || !Array.isArray(values)) {
      this.selectedItems = [];
      this.selectedValues = [];
      return;
    }

    this.selectedValues = [...values];
    this.selectedItems = this.options.filter(option =>
      values.includes(option[this.optionValue])
    );
  }

  getDisplayLabel(): string {
    if (this.selectedItems.length === 0) {
      return this.defaultLabel;
    }

    if (this.selectedItems.length === 1) {
      return this.selectedItems[0][this.optionLabel];
    }

    return `${this.selectedItems.length} elementos seleccionados`;
  }

  // Implementación de ControlValueAccessor
  writeValue(values: any[]): void {
    this.updateSelectedFromValues(values);
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onClickOutside(event: Event) {
    if (this.isOpen) {
      this.isOpen = false;
      this.onTouchedCallback();
    }
  }

  // Evita propagar el evento click desde el filtro al contenedor
  onFilterClick(event: Event) {
    event.stopPropagation();
  }
}



// WEBPACK FOOTER //
// D:/inetpub/wwwroot/kaizen-ng/src/app/modules/employee/pages/profile-surveis/custom-multiselect/custom-multiselect.component.ts